/*
 * Copyright '2024' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { MfeFramework } from "si-mfes-library/framework/mfe-framework";
import { ILocalizationService } from "sirius-platform-support-library/shared/localization/localization-service.interface";
import { sanitizeUrl } from "@braintree/sanitize-url";

export class LanguageService {
  private readonly framework: MfeFramework;
  private readonly localizationService: ILocalizationService;
  private static readonly SIRIUS_LOCAL_KEY = "sirius.localization.locale-code";

  constructor(
    framework: MfeFramework,
    localizationService: ILocalizationService
  ) {
    this.framework = framework;
    this.localizationService = localizationService;
    this.localizationService.onLocaleChanged(
      this,
      "LanguageService",
      this.onLocaleChanged.bind(this)
    );
  }

  public bootstrap(): Promise<string> {
    return new Promise<string>((resolve) => {
      const currentLocale = this.framework.PreferredLocale.getLocale();

      const urlLocale = this.framework.Locales.getFromUrl();
      if (
        urlLocale &&
        this.framework.SupportedLocales.isLocaleSupported(urlLocale)
      ) {
        this.switchLocale(currentLocale, urlLocale, true, false, true, true);
        resolve(urlLocale);
        return;
      }

      const cookieLocale = this.framework.Locales.getFromCookie();
      if (this.framework.SupportedLocales.isLocaleSupported(cookieLocale)) {
        this.switchLocale(
          currentLocale,
          cookieLocale,
          true,
          false,
          false,
          true
        );
        resolve(cookieLocale);
        return;
      }

      const locale =
        currentLocale || this.framework.SupportedLocales.DEFAULT_LOCALE;

      this.switchLocale(currentLocale, locale, false, false, false, true);

      resolve(locale);
    });
  }

  public onLocaleChanged(locale: any): void {
    if (!locale?.currentLocaleCode) {
      return;
    }
    const languageFromUrl = this.getLanguageFromUrl();
    const currentLocale = this.framework.PreferredLocale.getLocale();
    locale = this.framework.SupportedLocales.normalizeLocale(
      locale?.currentLocaleCode
    );

    if (languageFromUrl && languageFromUrl !== locale) {
      this.onLanguageChanged(currentLocale, locale);
      return;
    }

    if (locale === currentLocale) {
      return;
    }

    this.switchLocale(currentLocale, locale, true, false, false, false);
  }

  private onLanguageChanged(currentLocale: string, locale: string): void {
    this.stripLanguageFromUrl();
    this.switchLocale(currentLocale, locale, true, true, false, false);
  }

  private stripLanguageFromUrl() {
    let currentHref = sanitizeUrl(location.href);
    currentHref = this.removeURLParameter(currentHref, "lang");
    history.replaceState(null, "", currentHref);
  }

  private removeURLParameter(url, parameter): string {
    const urlParts = url.split("?");

    if (urlParts.length >= 2) {
      const prefix = encodeURIComponent(parameter) + "=";
      const pars = urlParts[1].split(/[&;]/g);

      for (let i = pars.length; i-- > 0; ) {
        if (pars[i].lastIndexOf(prefix, 0) !== -1) {
          pars.splice(i, 1);
        }
      }

      return urlParts[0] + (pars.length > 0 ? "?" + pars.join("&") : "");
    }
    return url;
  }

  private getLanguageFromUrl(): string {
    return new URLSearchParams(window.location.search).get("lang");
  }

  public getLocale(): string {
    return this.framework.PreferredLocale.getLocale();
  }

  private switchLocale(
    oldLocale,
    newLocale: string,
    permanent = true,
    redirect = false,
    force = false,
    comingFromOutsideDomain = false
  ): void {
    if (!force && oldLocale === newLocale) {
      return;
    }
    const isLocaleSupported =
      this.framework.SupportedLocales.isLocaleSupported(newLocale);
    let locale = newLocale;
    if (!isLocaleSupported) {
      locale = this.framework.SupportedLocales.DEFAULT_LOCALE;
      this.localizationService.changeLocaleCode(locale);
    }
    this.framework.PreferredLocale.setLocale(locale, permanent, force);

    if (comingFromOutsideDomain) {
      window.localStorage.setItem(LanguageService.SIRIUS_LOCAL_KEY, locale);
      this.localizationService.changeLocaleCode(locale);
    }

    if (redirect === true) {
      this.framework.PreferredLocale.triggerLocaleChangedReload(
        oldLocale,
        newLocale
      );
    }
  }
}
